import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEntityPreview } from '../../services/cms/getEntityPreview';
import { trackEvent } from '../../utils/analytics';
import AppLink from '../shared/AppLink';
const slidesConfig = [
  {
    title: 'Check‑in and unwind at The Palmwood',
    timeframe: '4:00 PM – 5:30 PM',
    description: 'Arrive at your hideaway and let the quiet valley set the tone. Take a cool rinse, settle onto the lanai, and unpack just enough to feel at home. Set out your beach bag so sunset is easy.',
    why: '✨ A restorative pause balances the day and saves energy for golden hour.',
    entityRef: { catalog: 'lodging', slug: 'palmwood', link: '/lodging/palmwood', title: 'The Palmwood' }
  },
  {
    title: 'Alekoko (Menehune) Fishpond overlook',
    timeframe: '10:00 AM – 10:30 AM',
    description: 'Pause at the overlook to take in this ingenious ancient fishpond framed by the Huleʻia. A moment to reflect on the island’s layered craftsmanship and stewardship.',
    why: '✨ A concise heritage stop aligns with your interest in authentic, place-based history.',
    entityRef: { catalog: 'blog', slug: 'ancient-fishponds', link: '/blog/ancient-fishponds', title: 'Ancient Fishponds' }
  },
  {
    title: 'Kīlauea village lunch at Sushigirl Kaua’i',
    timeframe: '12:30 PM – 1:15 PM',
    description: 'Head back into Kīlauea for a lunch that embodies fresh, local ingredients and laid-back aloha spirit at Sushigirl. Toast the morning’s salt‑spray glow and refuel without rushing the day.',
    why: '✨ A local spot that features local flavors matches your dining style and keeps logistics easy near home base.',
    entityRef: { catalog: 'restaurant', slug: 'sushigirl-kauai', link: '/restaurants/sushigirl-kauai', title: 'Sushigirl Kauai' }
  },
  {
    title: 'Wide‑sand afternoon and sunset picnic at Polihale',
    timeframe: '3:30 PM – 7:30 PM',
    description: 'Walk the edge of the dunes and let the scale open up—the Na Pali cliffs fade north while the Pacific stretches forever west. Keep ocean time to ankle‑deep wading unless conditions are clearly calm; shorebreak can be powerful. Unfold your picnic as golden hour pours across the sand, then watch the sky bloom into sunset.',
    why: '✨ This captures the soulful, off‑grid West Side moment you came for—unhurried and spectacular.',
    entityRef: { catalog: 'activity', slug: 'polihale-state-park', link: '/activities/polihale-state-park', title: 'Polihale State Park' }
  },
  {
    title: 'Kauaʻi Coffee Estate espresso + orchard path',
    timeframe: '9:00 AM – 10:00 AM',
    description: 'Swing through the estate for a short self-guided stroll and a well-pulled espresso—stories of sugar-to-coffee transition are told along the orchard path. It’s a grounded taste of West Side agriculture with ocean breezes in the trees.',
    why: '✨ A history-rich, land-based stop adds cultural depth without straying far from your route.',
    entityRef: { catalog: 'activity', slug: 'kauai-coffee-plantation', link: '/activities/kauai-coffee-plantation', title: 'Kauaʻi Coffee Plantation' }
  },
  {
    title: 'Kōkeʻe Lodge lunch + meadow reset',
    timeframe: '1:05 PM – 2:05 PM',
    description: 'Refuel by the fireplace with hearty plates—think chili and cornbread, burgers, and pies—then linger a moment in the cool mountain air. The adjacent museum and meadow are perfect for an easy stroll and group reconnection.',
    why: '✨ A cozy, social lunch anchors the day and keeps both cars in the group on the same beat.',
    entityRef: { catalog: 'restaurant', slug: 'kokee-lodge', link: '/restaurants/kokee-lodge', title: 'Kōkeʻe Lodge' }
  }
];

const ItineraryAnatomy = ({
  primaryCtaLabel = 'Build Your Itinerary',
  onPrimaryCtaClick,
  ctaLocation = 'itinerary_anatomy_cta',
}) => {
  const slides = slidesConfig;
  const total = slides.length;
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  const safeIndex = useMemo(() => {
    if (total === 0) return 0;
    if (index < 0) return 0;
    if (index >= total) return total - 1;
    return index;
  }, [index, total]);

  const cardRef = useRef(null);
  const shouldScrollRef = useRef(false);

  const current = total > 0 ? slides[safeIndex] : null;
  const entityRef = current?.entityRef;
  const entityPanelId = useMemo(() => `itinerary-anatomy-entity-${safeIndex}`, [safeIndex]);
  const [entityExpanded, setEntityExpanded] = useState(false);
  const [entityDetails, setEntityDetails] = useState({});
  const [entityLoading, setEntityLoading] = useState({});
  const [entityError, setEntityError] = useState({});
  const entityKey = entityRef ? `${entityRef.catalog}:${entityRef.slug}` : null;
  const resolvedDetails = entityKey ? entityDetails[entityKey] : null;
  const resolvedLoading = entityKey ? !!entityLoading[entityKey] : false;
  const resolvedError = entityKey ? entityError[entityKey] : undefined;
  const displayTitle = resolvedDetails?.title || entityRef?.title || entityRef?.slug || 'Related highlight';
  const displaySummary = resolvedDetails?.summary;
  const displayLink = resolvedDetails?.link || entityRef?.link || null;
  const displayImage = resolvedDetails?.imageUrl || null;

  useEffect(() => {
    setEntityExpanded(false);
  }, [safeIndex]);

  const goPrev = () => {
    if (total === 0) return;
    shouldScrollRef.current = true;
    setIndex((prev) => (prev - 1 + total) % total);
  };

  const goNext = () => {
    if (total === 0) return;
    shouldScrollRef.current = true;
    setIndex((prev) => (prev + 1) % total);
  };

  useEffect(() => {
    if (!cardRef.current || !shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isSmallScreen = window.innerWidth < 640; // align to top on very small screens
    const blockMode = isSmallScreen ? 'start' : 'center';

    const doScroll = () => {
      if (!cardRef.current) return;
      try {
        cardRef.current.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: blockMode });
      } catch {
        const rect = cardRef.current.getBoundingClientRect();
        const absoluteY = window.pageYOffset + rect.top;
        const targetY = isSmallScreen
          ? absoluteY - 8
          : absoluteY - (window.innerHeight / 2 - rect.height / 2);
        window.scrollTo({ top: targetY, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      }
    };

    // Give React a couple of frames to render the new, collapsed card before measuring/scrolling
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => doScroll());
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [safeIndex]);

  useEffect(() => {
    if (!entityRef || !entityKey) return;
    if (entityDetails[entityKey] || entityLoading[entityKey]) return;

    let cancelled = false;

    setEntityLoading((prev) => ({ ...prev, [entityKey]: true }));
    setEntityError((prev) => {
      if (!prev[entityKey]) return prev;
      const { [entityKey]: _removed, ...rest } = prev;
      return rest;
    });

    getEntityPreview(entityRef.catalog, entityRef.slug)
      .then((details) => {
        if (cancelled) return;
        if (details) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('Loaded related entity preview', { entityKey, details });
          }
          setEntityDetails((prev) => ({
            ...prev,
            [entityKey]: {
              title: details.title,
              summary: details.summary,
              imageUrl: details.imageUrl || null,
              link: entityRef.link
            }
          }));
        } else {
          setEntityError((prev) => ({
            ...prev,
            [entityKey]: `No preview returned from Sanity for slug "${entityRef.slug}".`
          }));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch related entity preview', { entityRef, error });
        if (cancelled) return;
        setEntityError((prev) => ({
          ...prev,
          [entityKey]: `No preview returned from Sanity for slug "${entityRef.slug}".`
        }));
      })
      .finally(() => {
        if (cancelled) return;
        setEntityLoading((prev) => ({ ...prev, [entityKey]: false }));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, entityRef]);

  if (!current) {
    return null;
  }

  const {
    title,
    timeframe,
    description,
    why
  } = current;

  return (
    <section className="text-center">
      <div className="max-w-6xl mx-auto px-2 sm:px-4">
        <div className="space-y-2">
          <h2 className="text-3xl md:text-4xl font-semibold">Itinerary Anatomy</h2>
          <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-center text-white">
            Each day is broken into 6–8 blocks with detailed guidance, start and end times, an explanation for why this made the plan, and links to explore each suggestion more thoroughly.
          </p>
        </div>

        <p className="mt-8 text-xs text-white/60 text-center">Your itinerary will be generated from your interview — these are just examples to show the structure and level of detail.</p>

        <div className="mt-4">
          <div className="relative flex flex-col items-center gap-4">
            <article ref={cardRef} className="glass-card w-full max-w-2xl rounded-lg border border-white/10 bg-white/5 p-6 text-left">
              <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <h3 className="text-xl font-semibold text-white">{title}</h3>
                {timeframe && (
                  <div className="text-sm font-semibold text-emerald-300 whitespace-nowrap">{timeframe}</div>
                )}
              </header>

              {description && (
                <p className="mt-4 text-sm leading-relaxed text-white">
                  {description}
                </p>
              )}

              {why && (
                <p className="mt-3 text-xs italic text-emerald-300">✨ {why.replace(/^✨\s*/, '')}</p>
              )}

              {entityKey && (
                <div className="mt-6">
                  <div className="w-full rounded-lg border border-white/10 bg-white/5 md:max-w-2xl mx-auto">
                    <button
                      type="button"
                      onClick={() => setEntityExpanded((prev) => !prev)}
                      aria-expanded={entityExpanded}
                      aria-controls={entityPanelId}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-emerald-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 hover:text-white"
                    >
                      <span>{displayTitle}</span>
                      <svg
                        className={`h-4 w-4 transform transition-transform duration-200 ${entityExpanded ? 'rotate-180 text-white' : 'text-emerald-300 hover:text-white'}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 15l-5-5h10l-5 5z" fill="currentColor" />
                      </svg>
                    </button>
                    {entityExpanded && (
                      <div id={entityPanelId} className="px-3 pb-3 md:px-4 md:pb-4">
                        <div className="space-y-3">
                          {resolvedLoading && (
                            <p className="text-sm text-white/60">Loading highlight…</p>
                          )}
                          {!resolvedLoading && resolvedError && (
                            <p className="text-sm text-amber-300">{resolvedError}</p>
                          )}
                          {!resolvedLoading && !resolvedError && (
                            <>
                              <div className="overflow-hidden rounded-md border border-white/10 bg-white/5">
                                {displayImage ? (
                                  <img
                                    src={displayImage}
                                    alt={displayTitle}
                                    loading="lazy"
                                    className="w-full object-cover"
                                    style={{ aspectRatio: '4 / 3' }}
                                  />
                                ) : (
                                  <div className="flex h-40 items-center justify-center text-xs text-white/60">
                                    No image returned for this slug yet.
                                  </div>
                                )}
                              </div>
                              {displaySummary ? (
                                <p className="text-sm leading-relaxed text-white/70">{displaySummary}</p>
                              ) : (
                                <p className="text-sm text-white/60">Sanity preview didn’t include a summary for this slug.</p>
                              )}
                              {displayLink && (
                                <AppLink
                                  to={displayLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-sm font-semibold text-emerald-300 transition-colors hover:text-white"
                                >
                                  View full profile ↗
                                </AppLink>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>

            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={goPrev}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition-transform hover:-translate-y-0.5 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                aria-label="Show previous itinerary block example"
              >
                <span aria-hidden="true">‹</span>
              </button>
              <div className="flex items-center gap-2">
                {slides.map((_, dotIndex) => (
                  <button
                    key={`anatomy-dot-${dotIndex}`}
                    type="button"
                    onClick={() => {
                      shouldScrollRef.current = true;
                      setIndex(dotIndex);
                    }}
                    aria-label={`Show itinerary block example ${dotIndex + 1}`}
                    className={`h-2.5 w-2.5 rounded-full transition-colors ${dotIndex === safeIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/60'}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={goNext}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition-transform hover:-translate-y-0.5 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                aria-label="Show next itinerary block example"
              >
                <span aria-hidden="true">›</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            className="btn-primary-cta w-full sm:w-auto"
            onClick={() => {
              if (onPrimaryCtaClick) {
                onPrimaryCtaClick(ctaLocation, primaryCtaLabel);
                return;
              }
              trackEvent('interview_started', 'conversion', 'itinerary_anatomy_cta');
              navigate('/interview/wizard');
            }}
          >
            {primaryCtaLabel}
          </button>
        </div>
      </div>
    </section>
  );
};

export default ItineraryAnatomy;
